import type { PartnerType } from './shopCatalog';

export type SynergyTag = '射击' | '武斗' | '法术' | '支援';

export interface SynergySnapshot {
  tag: SynergyTag;
  count: number;
  tiers: number[];
  description: string;
}

export interface SynergyModifiers {
  shootingFireRateBonus: number;
  shootingProjectileSpeedBonus: number;
  shootingCritChanceBonus: number;
  shootingPierce: number;
  martialDamageBonus: number;
  martialRangeBonus: number;
  martialCritChanceBonus: number;
  martialKnockbackBonus: number;
  martialLifestealBonus: number;
  magicRangeBonus: number;
  magicDamageBonus: number;
  magicCooldownBonus: number;
  magicKillExplosionDamage: number;
  supportEffectBonus: number;
  supportCooldownBonus: number;
  supportDamageTakenReduction: number;
  supportRegenPerSecond: number;
}

const EMPTY_MODIFIERS: SynergyModifiers = {
  shootingFireRateBonus: 0,
  shootingProjectileSpeedBonus: 0,
  shootingCritChanceBonus: 0,
  shootingPierce: 0,
  martialDamageBonus: 0,
  martialRangeBonus: 0,
  martialCritChanceBonus: 0,
  martialKnockbackBonus: 0,
  martialLifestealBonus: 0,
  magicRangeBonus: 0,
  magicDamageBonus: 0,
  magicCooldownBonus: 0,
  magicKillExplosionDamage: 0,
  supportEffectBonus: 0,
  supportCooldownBonus: 0,
  supportDamageTakenReduction: 0,
  supportRegenPerSecond: 0,
};

const TAGS: readonly SynergyTag[] = ['射击', '武斗', '法术', '支援'];

export class SynergySystem {
  calculate(partners: readonly PartnerType[]) {
    const counts = this.countByTag(partners);
    const snapshots: SynergySnapshot[] = TAGS.map((tag) => {
      const count = counts[tag] ?? 0;
      return {
        tag,
        count,
        tiers: getActiveTiers(count),
        description: describeSynergy(tag, count),
      };
    });

    return {
      counts,
      snapshots,
      modifiers: getModifiers(counts),
    };
  }

  getTagCount(partners: readonly PartnerType[], tag: SynergyTag) {
    return this.countByTag(partners)[tag] ?? 0;
  }

  private countByTag(partners: readonly PartnerType[]) {
    const counts: Record<SynergyTag, number> = {
      射击: 0,
      武斗: 0,
      法术: 0,
      支援: 0,
    };

    for (const partner of partners) {
      for (const tag of getPartnerTags(partner)) {
        counts[tag] += 1;
      }
    }

    return counts;
  }
}

export function getPartnerTags(partner: PartnerType): SynergyTag[] {
  if (partner === 'gunner') return ['射击'];
  if (partner === 'engineer') return ['射击', '支援'];
  if (partner === 'sword' || partner === 'assassin' || partner === 'tank') return ['武斗'];
  if (partner === 'mage' || partner === 'summoner') return ['法术'];
  if (partner === 'nurse') return ['支援'];
  throw new Error(`Unknown partner for synergy: ${partner}`);
}

function getActiveTiers(count: number) {
  const tiers = [];
  if (count >= 2) tiers.push(2);
  if (count >= 3) tiers.push(3);
  if (count >= 4) tiers.push(4);
  return tiers;
}

function getModifiers(counts: Record<SynergyTag, number>): SynergyModifiers {
  const modifiers = { ...EMPTY_MODIFIERS };

  if (counts.射击 >= 2) modifiers.shootingFireRateBonus = 0.15;
  if (counts.射击 >= 3) modifiers.shootingProjectileSpeedBonus = 0.3;
  if (counts.射击 >= 4) {
    modifiers.shootingPierce = 1;
    modifiers.shootingCritChanceBonus = 0.1;
  }

  if (counts.武斗 >= 2) modifiers.martialDamageBonus = 0.15;
  if (counts.武斗 >= 3) {
    modifiers.martialRangeBonus = 0.25;
    modifiers.martialLifestealBonus = 1;
  }
  if (counts.武斗 >= 4) {
    modifiers.martialCritChanceBonus = 0.15;
    modifiers.martialKnockbackBonus = 0.5;
  }

  if (counts.法术 >= 2) modifiers.magicRangeBonus = 0.2;
  if (counts.法术 >= 3) {
    modifiers.magicCooldownBonus = 0.2;
    modifiers.magicDamageBonus = 0.2;
  }
  if (counts.法术 >= 4) modifiers.magicKillExplosionDamage = 6;

  if (counts.支援 >= 2) modifiers.supportEffectBonus = 0.3;
  if (counts.支援 >= 3) {
    modifiers.supportCooldownBonus = 0.2;
    modifiers.supportDamageTakenReduction = 0.15;
  }
  if (counts.支援 >= 4) modifiers.supportRegenPerSecond = 3;

  return modifiers;
}

function describeSynergy(tag: SynergyTag, count: number) {
  const tiers = getActiveTiers(count);
  const active = tiers.length > 0 ? tiers.join('/') : '-';
  return `${tag} ${count} (${active})`;
}
